import { gql } from 'graphql.macro';
import { connectedLabFragment } from 'apollo/fragments/organization';

export const nameFragment = gql`
    fragment nameFields on Name {
        firstName
        lastName
    }
`;

export const linkedOrganizationFragment = gql`
    fragment linkedOrganizationFields on LinkedOrganization {
        organization {
            _id
            name
            type
            local
            active
            isWhiteLabeled
            connectedLabs {
                ...connectedLabFields
            }
        }
        role
        active
        type
    }
    ${connectedLabFragment}
`;

const userNotificationFragment = gql`
    fragment userNotificationFields on GeneralNotifications {
        newMessage
        orderReceivedByLab
        orderSentByLab
        orderDeclinedByLab
        deliveryDateChanged
        orderNotReceived {
            active
            days
        }
    }
`;

const userEmailNotificationFragment = gql`
    fragment userEmailNotificationFields on EmailNotifications {
        type
        otherEmail
        newMessage
        orderReceivedByLab
        orderSentByLab
        orderDeclinedByLab
    }
`;

export const localeFormatFragment = gql`
    fragment localeFormatFields on LocaleFormat {
        language
        date
        number
    }
`;

export const userFragment = gql`
    fragment userFields on User {
        _id
        type
        isAdmin
        role
        email
        active
        isShared
        authMethod
        syncid
        isTotpEnabled
        title
        fullName
        systemSetting {
            clinic {
                _id
                name
                isNotPickUpAndDeliveryPlace
            }
            autoArchiveOrderDays
        }
        localeFormat {
            ...localeFormatFields
        }
        viewSettings {
            deliveryDatePassed
            orderModified
        }
        orderListSettings {
            selectedMainProfileId
            selectedWorkflowProfileId
            hasSelectedMainProfile
            hasSelectedWorkflowProfile
            columnWidths {
                profileId
                widths {
                    columnKey
                    width
                }
            }
        }
        name {
            ...nameFields
        }
        generalNotifications {
            ...userNotificationFields
        }
        emailNotifications {
            ...userEmailNotificationFields
        }
        constructionSetting {
            ponticDesign
            metalDesign
            occlusionContact
            contactType
        }
        linkedOrganizations {
            ...linkedOrganizationFields
        }
        personalIdentityNumber
    }
    ${nameFragment}
    ${userNotificationFragment}
    ${userEmailNotificationFragment}
    ${linkedOrganizationFragment}
    ${localeFormatFragment}
`;

export const permissionFragment = gql`
    fragment permissionFields on Permissions {
        role
        description
        type
        removed
        active
        permissions {
            EDIT_ACTIONS
            GET_ORDERS
            REPORTS
            GET_USERS
            LIST_PATIENTS
        }
    }
`;

export const userFragmentForCache = gql`
    fragment userForCacheFields on User {
        _id
        type
        isAdmin
        role
        isShared
        authMethod
        syncid
        localeFormat {
            ...localeFormatFields
        }
        linkedOrganizations {
            ...linkedOrganizationFields
        }
        viewSettings {
            deliveryDatePassed
            orderModified
        }
        orderListSettings {
            selectedMainProfileId
            selectedWorkflowProfileId
            hasSelectedMainProfile
            hasSelectedWorkflowProfile
            columnWidths {
                profileId
                widths {
                    columnKey
                    width
                }
            }
        }
        name {
            ...nameFields
        }
        organization {
            _id
        }
        generalNotifications {
            ...userNotificationFields
        }
        permissions {
            ...permissionFields
        }
    }
    ${nameFragment}
    ${userNotificationFragment}
    ${permissionFragment}
    ${linkedOrganizationFragment}
    ${localeFormatFragment}
`;

export const dentistFragment = gql`
    fragment dentistFields on Dentist {
        _id
        fullName
        name {
            ...nameFields
        }
        email
        authMethod
        syncid
        personalIdentityNumber
        title
        phone
        comments
        role
        organization {
            _id
            name
            type
            isWhiteLabeled
            connectedLabs {
                isDisabled
                lab {
                    _id
                    name
                    hexCode
                    textCode
                }
            }
        }
        active
        systemSetting {
            clinic {
                _id
                name
            }
        }
        local
        linkedOrganizations {
            ...linkedOrganizationFields
        }
        whiteLabelAccess
        dentistNotes {
            lab
            clinic
            note
        }
        dentistLocalIds {
            lab
            clinic
            localId
        }
        labDentistDefaultClinics {
            mainClinic
            defaultClinic {
                _id
                name
            }
        }
        enableUserSync
    }
    ${nameFragment}
    ${linkedOrganizationFragment}
`;

export const technicianFragment = gql`
    fragment technicianFields on Technician {
        _id
        fullName
        name {
            ...nameFields
        }
        organization {
            _id
            name
            type
            isWhiteLabeled
            connectedLabs {
                ...connectedLabFields
            }
        }
        email
        role
        active
        isShared
        authMethod
        syncid
        enableUserSync
        personalIdentityNumber
        linkedOrganizations {
            ...linkedOrganizationFields
        }
    }
    ${nameFragment}
    ${linkedOrganizationFragment}
    ${connectedLabFragment}
`;

export const linkedUserFragment = gql`
    fragment linkedUserFields on User {
        _id
        email
        fullName
        role
        name {
            ...nameFields
        }
    }
    ${nameFragment}
`;
